import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import md5 from 'md5';

const auth = {
    accessToken: null, // Токен доступа
    refreshToken: null, // Токен обновления
    currentUser: null, // Текущий пользователь
    userData: {}, // Данные пользователя
    baseUrl: `${process.env.VUE_APP_URL_GATEWAY}/v1/auth`,
    loginUrl: `${process.env.VUE_APP_URL_GATEWAY}/v1/auth/init`,
    refreshTokenUrl: `${process.env.VUE_APP_URL_GATEWAY}/v1/auth/refresh`,
    logoutTokenUrl: `${process.env.VUE_APP_URL_GATEWAY}/v1/auth/logout`,

    // Получение экземпляра axios с заголовком авторизации
    getAxiosInstance: (baseUrl) => {
        const axiosInstance = axios.create({
            baseURL: baseUrl,
            timeout: 30000,
        });

        // Добавляем заголовок авторизации для всех запросов
        axiosInstance.interceptors.request.use((config) => {
            config.headers['Authorization'] = `Bearer ${auth.getAccessToken()}`;
            return config;
        });

        // Добавляем перехватчик ошибок для всех запросов
        axiosInstance.interceptors.response.use(
            (response) => response,
            async (error) => {
                const originalRequest = error.config;

                // Если запрос не получил ответа, возвращаем ошибку как есть
                if (!error.response || !error.response.status) {
                    return Promise.reject(error);
                }

                // Проверяем, что ошибка 401 Unauthorized и это не повторная попытка
                if (error.response.status === 401 && !originalRequest._retry) {
                    // Если это первая попытка повторить запрос
                    originalRequest._retry = true;

                    try {
                        // Пытаемся обновить токен
                        await auth.refreshAccessToken();
                    } catch (e) {
                        // Если обновление токена не удалось, выходим из приложения
                        const newError = new Error('Unauthorized');
                        return Promise.reject(newError);
                    }


                    // После обновления токена повторяем оригинальный запрос с новым токеном
                    originalRequest.headers['Authorization'] = `Bearer ${auth.getAccessToken()}`;
                    return axiosInstance(originalRequest);
                }

                // Если ошибка не 401 или повторная попытка, возвращаем ошибку как есть
                return Promise.reject(error);
            }
        );

        return axiosInstance;
    },

    login: async (p) => {
        auth.clearAll();

        try {
           const response = await axios.post(auth.loginUrl, {
                "email": p.email,
                "phone": p.phone,
                "code": p.code,
                "password": md5(p.password)
            }, {
                timeout: 10000 
            });

            if (response.data && response.data.data) {
                const { access_token, refresh_token } = response.data.data;
                auth.setAccessToken(access_token);
                auth.setRefreshToken(refresh_token);
            }
        } catch (error) {
            if (error.response && error.response.data.error) {
                const err = error.response.data.error
                if (err.code == "PHC010") {
                    p.onError("identity service error");
                    return;
                }

                p.onError(error.response.data.error.message);
                return;
            }

            p.onError("logIn failed");
            console.error('LogIn error:', error);
        }

        p.onSuccess(auth.getCurrentUser())
    },

    refreshAccessToken: async () => {
        if (!auth.getRefreshToken()) {
            auth.clearAll();
            return false;
        }

        try {
            const response = await axios.post(auth.refreshTokenUrl, {
                "refresh_token": auth.getRefreshToken(),
            }, {
                timeout: 10000 
            });

            if (response.data && response.data.data.access_token) {
                auth.setAccessToken(response.data.data.access_token);
            }
        } catch (error) {
            if (error.response && error.response.data.error) {
                console.error('refresh token error: ' + error.response.data.error);
                return false;
            }

            console.error('refresh error: ' + error.message);
            return false;
        }
        return true;
    },

    logout: async () => {
        try {
            await auth.getAxiosInstance(auth.logoutTokenUrl).post(null, {
                "refresh_token": auth.getRefreshToken(),
            }, {
                timeout: 10000 
            });
        } catch (error) {
            console.error('LogOut error:', error);
        }
        auth.clearAll();
    },

    ////////////////////////////////////////////////////////////////////////////

    getCurrentUserData: () => {
        if (auth.userData) {
            return auth.userData;
        }

        const user = localStorage.getItem('currentUser');
        if (user) {
            auth.currentUser = JSON.parse(user);

            if (!auth.currentUser) {
                auth.clearAll();
                return null;
            }

            if (auth.currentUser.exp < Date.now() / 1000) {
                auth.clearAll();
                return null;
            }

            auth.userData = auth.currentUser.payload.user;
            // if exipred
            return auth.userData;
        }

        return null;
    },

    getCurrentUser: () => {
        if (auth.currentUser) {
            return auth.currentUser;
        }

        const user = localStorage.getItem('currentUser');
        if (user) {
            auth.currentUser = JSON.parse(user);
            auth.userData = auth.currentUser.payload.user;
            // if exipred
            if (auth.currentUser.exp < Date.now() / 1000) {
                auth.clearAll();
                return null;
            }

            return auth.currentUser;
        }

        return null;
    },

    getAccessToken: () => {
        if (auth.accessToken) {
            return auth.accessToken;
        }

        const token = localStorage.getItem('accessToken');
        if (token) {
            auth.accessToken = token;
            return auth.accessToken;
        }

        return '';
    },

    getRefreshToken: () => {
        if (auth.refreshToken) {
            return auth.refreshToken;
        }

        const token = localStorage.getItem('refreshToken');
        if (token) {
            auth.refreshToken = token;
            return auth.refreshToken;
        }

        return '';
    },

    setAccessToken: (token) => {
        auth.accessToken = token;
        localStorage.setItem('accessToken', token);
        auth.setCurrentUser(jwtDecode(token));
    },

    setRefreshToken: (token) => {
        auth.refreshToken = token;
        localStorage.setItem('refreshToken', token);
    },

    setCurrentUser: (userData) => {
        // const userPayload = userData.payload.user
        // user = {
        //     id: userPayload.id,
        //     email: userPayload.email,
        //     phone: userPayload.phone,
        //     code: userPayload.code,
        //     permissions: userPayload.permissions,
        // }
        auth.currentUser = userData;
        localStorage.setItem('currentUser', JSON.stringify(userData));
    },

    clearAll: () => {
        auth.accessToken = '';
        auth.refreshToken = '';
        auth.currentUser = null;

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('currentUser');
        localStorage.removeItem('currentFirm');
    },
};

export default auth;